import axios from 'axios';
import orderBy from 'lodash/orderBy';
import PropTypes from 'prop-types';
import React from 'react';
import DataTable from 'react-data-table-component';
import ReactNotification from 'react-notifications-component';
import { PopupboxManager, PopupboxContainer } from 'react-popupbox';

import { addRetranscodeRequestSubmittedNotification } from '../../../__util__/notifications';

class AssetTable extends React.Component {
  static propTypes = {
    authenticityToken: PropTypes.string,
  };

  constructor(props) {
    super(props);

    this.state = {
      accounts: [],
      currentAccount: {},
      currentLibrary: {},
      searchString: '',
      tableKey: 0,
    };

    this.handleAccountSelect = this.handleAccountSelect.bind(this);
    this.handleLibrarySelect = this.handleLibrarySelect.bind(this);
    this.fetchLibraryInfo = this.fetchLibraryInfo.bind(this);
    this.handleRowClick = this.handleRowClick.bind(this);
    this.openPopupbox = this.openPopupbox.bind(this);
    this.retranscodeAsset = this.retranscodeAsset.bind(this);

    this.handleChange = this.handleChange.bind(this);
    this.sortFunction = this.sortFunction.bind(this);
    this.orderByFileSize = this.orderByFileSize.bind(this);

    this.notificationDOMRef = React.createRef();
    // This method has to be bound to class so jest tests can spy on it
    this.addRetranscodeRequestSubmittedNotification = addRetranscodeRequestSubmittedNotification.bind(this);
  }

  componentDidMount() {
    axios.get('/manage/accounts/react_accounts.json').then((res) => {
      const accounts = res.data;
      const currentAccount = accounts && accounts[0] ? accounts[0] : {};
      const sortedLibraries = currentAccount && currentAccount.libraries ? currentAccount.libraries : {};
      const currentLibrary = sortedLibraries && sortedLibraries[0] ? sortedLibraries[0] : null;
      this.setState({
        accounts,
        currentAccount,
        currentLibrary,
      });

      if (currentLibrary) {
        this.fetchLibraryInfo(currentLibrary.id);
      }
    });
  }

  generateRandomId() {
    return window.crypto.getRandomValues(new Uint32Array(1))[0];
  }

  handleAccountSelect(event) {
    const { accounts } = this.state;
    const id = parseInt(event.target.value);
    const currentAccount = accounts.filter((acc) => acc.id === id)[0];

    // Randomly update the table key to force data table to rerender
    // Otherwise the table stores pagination from one account to another
    // So viewing Account 1/Library 1 (items 11 - 21 out of 37)
    // and switching to Account 2/Library 1 (11 - 21 out of 5)
    // renders an unexpectedly blank table since the pagination is higher than available elements
    // rerendering the data table ensures pagination always resets to (1 - 10 out of X)
    const currentLibrary = currentAccount && currentAccount.libraries ? currentAccount.libraries[0] : {};
    this.setState({
      tableKey: this.generateRandomId(),
      currentAccount,
      currentLibrary,
    });

    if (currentLibrary) {
      this.fetchLibraryInfo(currentLibrary.id);
    }
  }

  handleLibrarySelect(event) {
    const { currentAccount } = this.state;
    if (currentAccount.libraries && currentAccount.libraries[0]) {
      const id = parseInt(event.target.value);
      const currentLibrary = currentAccount.libraries.filter((lib) => lib.id === id)[0];

      // Randomly update the table key to force data table to rerender
      // Otherwise the table stores pagination from one library to another
      // So viewing Library 1 (items 11 - 21 out of 37)
      // and switching to Library 2 (11 - 21 out of 5)
      // renders an unexpectedly blank table since the pagination is higher than available elements
      // rerendering the data table ensures pagination always resets to (1 - 10 out of X)
      this.setState({
        tableKey: this.generateRandomId(),
        currentLibrary,
      });

      this.fetchLibraryInfo(currentLibrary.id);
    } else {
      this.setState({ currentLibrary: {} });
    }
  }

  fetchLibraryInfo(libraryId) {
    axios.get(`/manage/accounts/react_library/${libraryId}.json`).then((res) => {
      this.setState({
        currentLibrary: res.data,
      });
    });
  }

  handleRowClick(row) {
    if (row && row.fileType === 'video' && row.isSoftDeleted === 'No') {
      this.openPopupbox(row);
    }
  }

  /* istanbul ignore next */
  openPopupbox(row) {
    PopupboxManager.open({
      content: (
        <div>
          <h3 className="text-center">Asset Re-transcode</h3>
          <p className="text-center">Would you like to request a transcode of this asset?</p>
          <p>
            <b>ID:</b> {row.id}
          </p>
          <p>
            <b>Title:</b> {row.title}
          </p>
          <p>
            <b>File Type:</b> {row.fileType}
          </p>
          <p>
            <b>Original File Size:</b> {row.originalFileSize}
          </p>
          <div className="text-center">
            <button
              className="button btn btn-primary"
              onClick={() => {
                // TODO: Figure out how to test callbacks within content provided modal
                PopupboxManager.close();
              }}
            >
              Cancel
            </button>
            &nbsp; &nbsp;
            <button
              className="button btn btn-success"
              onClick={() => {
                // TODO: Figure out how to test callbacks within content provided modal
                this.retranscodeAsset(row.id);
                PopupboxManager.close();
              }}
            >
              Retranscode
            </button>
          </div>
        </div>
      ),
    });
  }

  retranscodeAsset(assetId) {
    const { authenticityToken } = this.props;

    axios
      .post(`/manage/assets/${assetId}/transcode`, {
        authenticity_token: authenticityToken,
      })
      .then((res) => {
        const ref = this.notificationDOMRef.current;
        this.addRetranscodeRequestSubmittedNotification(ref);
      });
  }

  handleChange(event) {
    this.setState({
      tableKey: this.generateRandomId(),
      searchString: event.target.value,
    });
  }

  sortFunction(rows, field = '', direction = 'asc') {
    if (['originalFileSize', 'transcodeFileSize', 'transcodeHdFileSize'].includes(field)) {
      return this.orderByFileSize(rows, field, direction);
    }

    return orderBy(rows, field, direction);
  }

  // Order an array of file sizes e.g. ['3.4 MB', '2 KB']
  // taking into account the different magnitudes of file size
  // deliberately matches the output of Rails method number_to_human_size
  // https://apidock.com/rails/v5.2.3/ActionView/Helpers/NumberHelper/number_to_human_size
  orderByFileSize(rows, field, direction) {
    const newRows = rows.sort((a, b) => {
      const magnitudeA = a[field].split(' ')[1]; // size suffix e.g. MB
      const magnitudeB = b[field].split(' ')[1];

      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB'];
      if (sizes.indexOf(magnitudeA) < sizes.indexOf(magnitudeB)) {
        return -1;
      }
      if (sizes.indexOf(magnitudeA) > sizes.indexOf(magnitudeB)) {
        return 1;
      }

      // Tiebreak elements with same suffix by integer size
      return parseFloat(a[field].split(' ')[0]) - parseFloat(b[field].split(' ')[0]);
    });

    return direction === 'desc' ? newRows.reverse() : newRows;
  }

  render() {
    const { accounts, currentAccount, currentLibrary, searchString, tableKey } = this.state;
    const currentLib = currentLibrary || {};
    const allRows = currentLib.assets ? currentLib.assets : [];
    const rows = allRows.filter((r) => {
      return Object.values(r)
        .map((v) => {
          return v.toString().toLowerCase();
        })
        .some((v) => {
          return v.includes(searchString.toLowerCase());
        });
    });

    const columns = [
      {
        name: 'Asset ID',
        selector: 'id',
        sortable: true,
        minWidth: 20,
        wrap: true,
      },
      {
        name: 'Title',
        selector: 'title',
        sortable: true,
        width: 400,
        wrap: true,
      },
      {
        name: 'File Type',
        selector: 'fileType',
        sortable: true,
        minWidth: 50,
        wrap: true,
      },
      {
        name: 'Original',
        selector: 'originalFileSize',
        sortable: true,
        minWidth: 50,
        wrap: true,
      },
      {
        name: 'Web Transcode',
        selector: 'transcodeFileSize',
        sortable: true,
        minWidth: 50,
        wrap: true,
      },
      {
        name: 'HD Transcode',
        selector: 'transcodeHdFileSize',
        sortable: true,
        minWidth: 50,
        wrap: true,
      },
      {
        name: 'Soft Deleted?',
        selector: 'isSoftDeleted',
        sortable: true,
        minWidth: 50,
        wrap: true,
      },
    ];

    // IMPORTANT NOTE
    // Behind the scenes, all of the data being grabbed is manipulated based on the Chillipharm Account model
    // For instance the relevant libraries to display are taken from their association to the currently selected Account
    // However, for Chillipharm managers it's actually easier to reference the trial_name, which is an attribute of Account
    // Trial names are guaranteed to be unique (by the pharmaceutical industry, not programatically), where Account names are not
    // One real-world client can have multiple Chillipharm Accounts, each with its own trial_name
    // This is essentially a design oversight - Chillipharm was not coded originally to have a single Account model with multiple associated Trials
    // but instead it's necessary to create a new Account for each new real-world trial
    // and then give each related Account the same .company_name programatically but distinguish them by their .trial_name
    // These Accounts are associated with each other in the real world (i.e. they're the same client that Chillibean does business with), but not programatically
    // E.g. Account1 = id: 1, company_name: Roche, trial_name: Superdrug1234
    // Account2 = id: 2, company_name: Roche, trial_name: AmazingCure999
    // The upshot of all this is that the trial name is displayed in the dropdown for ease of use,
    // but in the code is actually manipulating the currently selected account.id and information, from which the related library data flows
    // Be very clear about this distinction before making changes
    return (
      <div>
        <ReactNotification ref={this.notificationDOMRef} />
        <div className="well clearfix">
          <div className="form-group">
            <label className="control-label">Trial Name</label>
            <select id="#accountSelect" onChange={this.handleAccountSelect} className="form-control">
              {accounts &&
                accounts.length &&
                accounts.map((acc) => {
                  return (
                    <option key={acc.id} value={acc.id}>
                      {acc.trial_name}
                    </option>
                  );
                })}
            </select>
            <hr />
          </div>

          <div className="form-group">
            <label className="control-label">Library</label>
            <select id="#librarySelect" onChange={this.handleLibrarySelect} className="form-control">
              {currentAccount &&
                currentAccount.libraries &&
                currentAccount.libraries.map((lib) => {
                  return (
                    <option key={lib.id} value={lib.id}>
                      {lib.name}
                    </option>
                  );
                })}
            </select>
            <hr />
          </div>
        </div>

        <div className="text-center">
          <p>
            Click on any row with <b>File Type 'video'</b> and <b>Soft Deleted? 'No'</b> to request a re-transcode.
          </p>
          <input type="text" placeholder="Search..." value={this.state.searchString} onChange={this.handleChange} />
        </div>
        <DataTable
          key={tableKey}
          columns={columns}
          data={rows}
          pagination
          paginationRowsPerPageOptions={[10, 20, 50, 100]}
          responsive={true}
          noHeader
          highlightOnHover
          sortFunction={this.sortFunction}
          onRowClicked={(row) => {
            this.handleRowClick(row);
          }}
        />
        <PopupboxContainer />
      </div>
    );
  }
}

export default AssetTable;
